import React, { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import {
  Box,
  Button,
  TextField,
  DatePicker,
  Dropdown,
  IconButton,
  Toast
} from 'monday-ui-react-core';
import { Add, Delete } from 'monday-ui-react-core/icons';
import waveClient from '../../services/wave/waveClient';
import mondayClient from '../../services/monday/mondayClient';

function InvoiceForm() {
  const navigate = useNavigate();
  const { id } = useParams();
  const [loading, setLoading] = useState(false);
  const [customers, setCustomers] = useState([]);
  const [invoice, setInvoice] = useState({
    customerId: '',
    date: new Date(),
    dueDate: new Date(),
    items: [{ description: '', quantity: 1, rate: 0 }],
    notes: ''
  });

  useEffect(() => {
    loadCustomers();
    if (id) {
      loadInvoice();
    }
  }, [id]);

  const loadCustomers = async () => {
    try {
      const data = await waveClient.getCustomers();
      setCustomers(data.map(customer => ({
        value: customer.id,
        label: customer.name
      })));
    } catch (error) {
      console.error('Error loading customers:', error);
    }
  };

  const loadInvoice = async () => {
    try {
      setLoading(true);
      const data = await waveClient.getInvoiceById(id);
      setInvoice({
        customerId: data.customer.id,
        date: new Date(data.date),
        dueDate: new Date(data.dueDate),
        items: data.items,
        notes: data.notes
      });
    } catch (error) {
      console.error('Error loading invoice:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleAddItem = () => {
    setInvoice(prev => ({
      ...prev,
      items: [...prev.items, { description: '', quantity: 1, rate: 0 }]
    }));
  };

  const handleRemoveItem = (index) => {
    setInvoice(prev => ({
      ...prev,
      items: prev.items.filter((_, i) => i !== index)
    }));
  };

  const handleItemChange = (index, field, value) => {
    setInvoice(prev => ({
      ...prev,
      items: prev.items.map((item, i) => 
        i === index ? { ...item, [field]: value } : item
      )
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      setLoading(true);
      const response = id
        ? await waveClient.updateInvoice(id, invoice)
        : await waveClient.createInvoice(invoice);

      // Create item in Monday.com board
      await mondayClient.createItem(
        process.env.REACT_APP_MONDAY_BOARD_ID,
        `Invoice #${response.invoiceNumber}`,
        {
          status: { label: 'Draft' },
          date: invoice.date,
          customer: customers.find(c => c.value === invoice.customerId)?.label,
          amount: calculateTotal()
        }
      );

      Toast.show({ 
        message: `Invoice ${id ? 'updated' : 'created'} successfully!`,
        type: Toast.types.POSITIVE 
      });
      navigate('/invoices');
    } catch (error) {
      console.error('Error saving invoice:', error);
      Toast.show({ 
        message: 'Error saving invoice',
        type: Toast.types.NEGATIVE 
      });
    } finally {
      setLoading(false);
    }
  };

  const calculateTotal = () => {
    return invoice.items.reduce((total, item) => 
      total + (item.quantity * item.rate), 0
    );
  };

  return (
    <Box className="invoice-form" padding={16}>
      <form onSubmit={handleSubmit}>
        <Box className="form-section">
          <Dropdown
            placeholder="Select Customer"
            value={invoice.customerId}
            options={customers}
            onChange={(value) => setInvoice(prev => ({ ...prev, customerId: value }))}
            required
          />
          <DatePicker
            placeholder="Invoice Date"
            value={invoice.date}
            onChange={(date) => setInvoice(prev => ({ ...prev, date }))}
            required
          />
          <DatePicker
            placeholder="Due Date"
            value={invoice.dueDate}
            onChange={(date) => setInvoice(prev => ({ ...prev, dueDate: date }))}
            required
          />
        </Box>

        <Box className="items-section">
          {invoice.items.map((item, index) => (
            <Box key={index} className="item-row">
              <TextField
                placeholder="Description"
                value={item.description}
                onChange={(value) => handleItemChange(index, 'description', value)}
                required
              />
              <TextField
                type="number"
                placeholder="Quantity"
                value={item.quantity}
                onChange={(value) => handleItemChange(index, 'quantity', Number(value))}
                required
              />
              <TextField
                type="number"
                placeholder="Rate"
                value={item.rate}
                onChange={(value) => handleItemChange(index, 'rate', Number(value))}
                required
              />
              <IconButton
                icon={Delete}
                onClick={() => handleRemoveItem(index)}
                disabled={invoice.items.length === 1}
              />
            </Box>
          ))}
          <Button
            leftIcon={Add}
            kind="tertiary"
            onClick={handleAddItem}
          >
            Add Item
          </Button>
        </Box>

        <TextField
          placeholder="Notes"
          value={invoice.notes}
          onChange={(value) => setInvoice(prev => ({ ...prev, notes: value }))}
          type="textarea"
        />

        <Box className="form-actions">
          <Button
            type="submit"
            disabled={loading}
          >
            {loading ? 'Saving...' : (id ? 'Update Invoice' : 'Create Invoice')}
          </Button>
          <Button
            type="button"
            kind="tertiary"
            onClick={() => navigate('/invoices')}
          >
            Cancel
          </Button>
        </Box>
      </form>
    </Box>
  );
}

export default InvoiceForm; 